import { useMemo } from 'react'
import { IUserWithPortalAccess } from 'src/generated-types'
import { AppFeatureScope } from 'src/constants/appFeatureScope'
import { useUserCtx } from 'src/hooks/context-hooks'
import { useAppFeatureFlags } from 'src/hooks/useAppFeatureFlags'

export type UseAppFeatureOptions = {
  /** Optional portal permission (e.g. hasStorageAccess) in addition to the feature flag. */
  accessField?: keyof IUserWithPortalAccess
  /** global = main portals; customer = client admin; auto = detect from route. */
  scope?: AppFeatureScope
  customerId?: number
  /** Preferred: stable integration id from code; DB links it to a feature row. */
  integrationKey?: string
}

/** Resolves whether an integration (or legacy feature key) is enabled for the current context. */
export function useAppFeature(featureKeyOrIntegration: string, options?: UseAppFeatureOptions) {
  const { isEnabled, isIntegrationEnabled, isLoading: isLoadingFlags, customerId } = useAppFeatureFlags(
    options?.customerId,
    options?.scope,
  )
  const { user, isLoadingUser } = useUserCtx()

  const gateKey = options?.integrationKey ?? featureKeyOrIntegration

  const allowed = useMemo(() => {
    const featureOk = options?.integrationKey
      ? isIntegrationEnabled(options.integrationKey)
      : isEnabled(gateKey)
    if (!featureOk) {
      return false
    }
    if (options?.accessField) {
      return Boolean(user?.[options.accessField])
    }
    return true
  }, [gateKey, isEnabled, isIntegrationEnabled, options?.accessField, options?.integrationKey, user])

  return {
    allowed,
    isLoading: isLoadingFlags || isLoadingUser,
    customerId,
  }
}
